import type { UseQueryResult } from '@tanstack/react-query';

import { useModuleFilter } from '@/context/ModuleFilter';
import { usePermissions } from '@/context/Permissions';
import type { MetricRange } from '@/schemas/commons';
import { useAlerts } from '@/services/Alerts';
import { useConversations } from '@/services/Conversations';
import { useDependencies } from '@/services/Dependencies';
import { useMetrics } from '@/services/Metrics';
import { type PauseState, usePauseState, useSetPause } from '@/services/Pause';
import { useGraphRouting } from '@/services/Playground';

import { ALERTS_PAGE, ALERTS_PAGE_SIZE } from './Dashboard.constants';
import type { PauseControlProps } from './Dashboard.types';
import { pauseErrorKey } from './Dashboard.utils';

const usePauseControl = (pause: UseQueryResult<PauseState>): PauseControlProps => {
	const { permissions } = usePermissions();
	const setPause = useSetPause();
	const paused = pause.data?.paused ?? false;

	return {
		paused,
		canPause: permissions.pause === true,
		isPending: setPause.isPending,
		errorKey: pauseErrorKey(setPause.error),
		onConfirm: (reason) => setPause.mutate({ paused: !paused, reason }),
		onReset: setPause.reset,
	};
};

export const useDashboardData = (range: MetricRange) => {
	const { selectedModule } = useModuleFilter();
	const metrics = useMetrics({ range, moduleName: selectedModule });
	const recent = useConversations({ page: 1, pageSize: 5, moduleName: selectedModule });
	// Status-strip queries stay out of isLoading/isError so a slow one never blocks the page.
	const pause = usePauseState();
	const pauseControl = usePauseControl(pause);
	const dependencies = useDependencies();
	const alerts = useAlerts(ALERTS_PAGE, ALERTS_PAGE_SIZE);
	const routing = useGraphRouting();

	return {
		metrics,
		recent,
		pause,
		pauseControl,
		dependencies,
		alerts,
		routing,
		isLoading: metrics.isLoading || recent.isLoading,
		isError: metrics.isError || recent.isError,
	};
};
